import {
  AdminSubGroupStats,
  BotData,
  OrderTypes,
  LanguageNameSpaces,
} from "@shared/types";
import React from "react";
import Card from "common/Boxes/Card";
import Title from "common/Typography/Title";
import { useHistory } from "react-router-dom";
import { ADMIN_TRADING_CONFIGURATION_FORM_PATH } from "common/constants";
import { generatePath } from "react-router-dom";
import { getBotAmountDisplay } from "@shared/features/bots/utils";
import { BotCardSectionWrapper } from "features/bots/MyBots/common/styles";
import Flex from "common/Boxes/Flex";
import IconWithFallback from "Global/IconWithFallback";
import { useSharedBotSummary } from "@shared/features/bots/hooks";
import TrailingStopDisplay from "features/bots/BotDrawer/TrailingStopDisplay";
import ConfigurationItem from "features/admin/TradingConfiguration/ConfigurationItem";
import AdminStrategySummary from "features/admin/TradingConfiguration/AdminStrategySummary";
import Button from "common/Buttons/Button";
import styled, { css, useTheme } from "styled-components";
import { Pencil } from "lucide-react";
import Panel from "common/NewLook/Panel.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { GROUP_ID_QUERY_PARAM } from "features/bots/common/constants.ts";

interface ConfigurationCardProps {
  botData: BotData;
  isLeverage?: boolean;
  subGroup?: AdminSubGroupStats;
}

const ConfigurationCard = ({
  botData,
  isLeverage,
  subGroup,
}: ConfigurationCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const history = useHistory();
  const { isNewLook } = useTheme();
  const {
    trailingEntryDisplay,
    trailingTakeProfitDisplay,
    trailingStopDisplay,
    cancelOnProfitBeforeEntryDisplayData,
  } = useSharedBotSummary(botData, false, false, undefined, true);

  const onClick = () => {
    history.push(
      generatePath(ADMIN_TRADING_CONFIGURATION_FORM_PATH, {
        configurationType: isLeverage ? "leverage" : "spot",
      }) + `?${GROUP_ID_QUERY_PARAM}=${subGroup?.id.toString()}`
    );
  };

  const sections = (
    <>
      <ConfigurationSection>
        <SectionTitle>{t("tradingConfiguration.general")}</SectionTitle>
        <ConfigurationItem
          title={t("tradingConfiguration.amount")}
          value={getBotAmountDisplay(botData, true)}
        />
      </ConfigurationSection>
      <ConfigurationSection>
        <SectionTitle>{t("tradingConfiguration.entry")}</SectionTitle>
        <AdminStrategySummary botData={botData} orderType={OrderTypes.ENTRY} />
        <ConfigurationItem
          title={t("tradingConfiguration.trailingEntry")}
          value={trailingEntryDisplay}
        />
      </ConfigurationSection>
      <ConfigurationSection>
        <SectionTitle>{t("tradingConfiguration.takeProfit")}</SectionTitle>
        <AdminStrategySummary
          botData={botData}
          orderType={OrderTypes.TAKE_PROFIT}
        />
        <ConfigurationItem
          title={t("tradingConfiguration.trailingTakeProfit")}
          value={trailingTakeProfitDisplay}
        />
      </ConfigurationSection>
      <ConfigurationSection>
        <SectionTitle>{t("tradingConfiguration.stop")}</SectionTitle>
        <ConfigurationItem
          title={t("tradingConfiguration.trailingStop")}
          value={
            <TrailingStopDisplay
              botData={botData}
              displayData={trailingStopDisplay}
            />
          }
        />
      </ConfigurationSection>
      <ConfigurationSection>
        <SectionTitle>{t("tradingConfiguration.advanced")}</SectionTitle>
        <ConfigurationItem
          title={cancelOnProfitBeforeEntryDisplayData?.title}
          value={cancelOnProfitBeforeEntryDisplayData?.value}
        />
      </ConfigurationSection>
    </>
  );

  // New look: a panel - the exchange type as its title, Edit on the right,
  // then the settings in groups with small group labels.
  if (isNewLook) {
    return (
      <Panel
        title={
          isLeverage
            ? t("tradingConfiguration.leverage_exchanges")
            : t("tradingConfiguration.spot_exchanges")
        }
        actions={
          <Button size="small" onClick={onClick}>
            <Pencil size={14} strokeWidth={1.75} />
            {t("tradingConfiguration.edit")}
          </Button>
        }
      >
        {sections}
      </Panel>
    );
  }

  return (
    <Card>
      <Flex style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <Flex $justifyContent="flex-start">
          <IconWithFallback
            iconLink={subGroup?.image}
            iconStyle={{
              width: 32,
              height: 32,
              marginRight: 12,
              borderRadius: 6,
            }}
          />
          <Title level={2} $ellipsis={true} style={{ marginRight: 12 }}>
            {isLeverage
              ? t("tradingConfiguration.leverage_exchanges")
              : t("tradingConfiguration.spot_exchanges")}
          </Title>
        </Flex>
        <Button $type="secondary" onClick={onClick}>
          {t("tradingConfiguration.edit")}
        </Button>
      </Flex>
      <BotCardSectionWrapper $isMobile={false}>
        {sections}
      </BotCardSectionWrapper>
    </Card>
  );
};

export default ConfigurationCard;

const ConfigurationSection = styled.div`
  background-color: ${(p) => p.theme.colors.statisticsBgColor};
  padding: 16px;
  border-radius: 8px;
  &:not(:last-of-type) {
    margin-bottom: 16px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Groups separated by a hairline with 16px on both sides. */
      background: transparent;
      padding: 0;
      border-radius: 0;
      &:not(:last-of-type) {
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid ${getNewLookTokens(p.theme).hairline};
      }
    `}
`;
const SectionTitle = styled(Title).attrs({
  level: 3,
})`
  margin-bottom: 16px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        margin-bottom: 4px;
        ${newLookType.micro};
        color: ${getNewLookTokens(p.theme).muted};
      }
    `}
`;
